import React, { createContext, useContext, useState } from 'react';
import type { ReferralProgram } from '@/types/referral';

type DialogMode = 'create' | 'edit';

interface ReferralProgramEditContextType {
  isOpen: boolean;
  mode: DialogMode;
  editingProgram: ReferralProgram | null;
  openEditProgram: (program: ReferralProgram) => void;
  openCreateProgram: () => void;
  closeEditProgram: () => void;
}

const ReferralProgramEditContext = createContext<ReferralProgramEditContextType | undefined>(undefined);

export const ReferralProgramEditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<DialogMode>('create');
  const [editingProgram, setEditingProgram] = useState<ReferralProgram | null>(null);

  const openEditProgram = (program: ReferralProgram) => {
    setEditingProgram(program);
    setMode('edit');
    setIsOpen(true);
  };

  const openCreateProgram = () => {
    setEditingProgram(null);
    setMode('create');
    setIsOpen(true);
  };

  const closeEditProgram = () => {
    setIsOpen(false);
    setEditingProgram(null);
  };

  return (
    <ReferralProgramEditContext.Provider
      value={{
        isOpen,
        mode,
        editingProgram,
        openEditProgram,
        openCreateProgram,
        closeEditProgram,
      }}
    >
      {children}
    </ReferralProgramEditContext.Provider>
  );
};

export const useReferralProgramEdit = () => {
  const context = useContext(ReferralProgramEditContext);
  if (context === undefined) {
    throw new Error('useReferralProgramEdit must be used within a ReferralProgramEditProvider');
  }
  return context;
};
